import { createContext, useContext } from 'react';
import type MapView from '@arcgis/core/views/MapView';
import type EsriMap from '@arcgis/core/Map';
import type VectorTileLayer from '@arcgis/core/layers/VectorTileLayer';
import { MapModes } from '../types';
import type { EsriLayerSelectionApi } from './features/useLayers';

export interface EsriSelectionApi {
	selectAssets(
		assetIds: string[],
		spanExtent?: boolean,
		layerId?: string | null,
	): void;
	clearSelection(): void;
	clearHighlight(): void;
}

export interface EsriMapContextValue {
	view: MapView | null;
	map: EsriMap | null;
	displayMode: MapModes;
	/** Layer selection + highlight API when useLayers is used. */
	layerSelectionApi: EsriLayerSelectionApi | null;
	/** Selection API for syncing UI (property pane, dummy panel) state. */
	selectionApi?: EsriSelectionApi | null;
	/** Resolve ESRI layer ids for a given dataServiceId. */
	getLayerIdsByServiceId?: (serviceId: string) => string[];
	/** True while the user is actively drawing a polygon on the map. */
	isDrawing: boolean;
	setIsDrawing: (v: boolean) => void;
}

const EsriMapContext = createContext<EsriMapContextValue>({
	view: null,
	map: null,
	displayMode: MapModes.Full,
	layerSelectionApi: null,
	selectionApi: null,
	getLayerIdsByServiceId: () => [],
	isDrawing: false,
	setIsDrawing: () => {},
});

export const useEsriMapContext = () => useContext(EsriMapContext);

export default EsriMapContext;
